import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import BrandMark from '../common/BrandMark';
import styles from './OnboardingTour.module.css';

// First-run product walkthrough. A single popup card steps through TOUR_STEPS with
// Previous / Next, an X to leave, and a row of progress dots. A step may name a real
// element to spotlight (steps[i].selector): when that element is on screen the card
// anchors beside it and everything else dims; otherwise the card centers on its own
// backdrop. Driven entirely by the buttons and the keyboard - it never clicks the app.

const GUTTER = 16; // keep the card this far from the viewport edge
const GAP = 14; // space between a spotlit element and the card
const RING_PAD = 8; // how far the glow ring sits outside the target

export default function OnboardingTour({ steps, isOpen, onClose }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null); // spotlight target rect, or null for centered
  const [pos, setPos] = useState(null); // { top, left } for the card
  const [tick, setTick] = useState(0); // bumps on scroll / resize to recompute
  const cardRef = useRef(null);

  const count = steps.length;
  const step = steps[index] || null;
  const isLast = index === count - 1;
  const isFirst = index === 0;

  const next = useCallback(() => {
    setIndex((i) => (i >= count - 1 ? i : i + 1));
  }, [count]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Restart at the first step every time the tour is opened.
  useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen]);

  // Keyboard: Esc leaves, arrows move. Rebinds per step so next/prev see the latest index.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') { if (!isLast) next(); }
      else if (e.key === 'ArrowLeft') { if (!isFirst) prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isLast, isFirst, next, prev, onClose]);

  // Recompute positions when the page moves under us.
  useEffect(() => {
    if (!isOpen) return undefined;
    const bump = () => setTick((t) => t + 1);
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
    };
  }, [isOpen]);

  // Resolve the spotlight target for the current step and scroll it into view.
  useLayoutEffect(() => {
    if (!isOpen || !step) return;
    let r = null;
    if (step.selector) {
      const el = document.querySelector(step.selector);
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
        const b = el.getBoundingClientRect();
        if (b.width > 0 && b.height > 0) r = b;
      }
    }
    setRect(r);
  }, [isOpen, index, tick, step]);

  // Place the card: beside the target if there is one, otherwise centered.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const card = cardRef.current;
    if (!card) return;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!rect) {
      setPos({
        top: Math.max(GUTTER, (vh - ch) / 2),
        left: Math.max(GUTTER, (vw - cw) / 2),
      });
      return;
    }

    const below = rect.bottom + GAP;
    const above = rect.top - GAP - ch;
    let top;
    if (below + ch <= vh - GUTTER) top = below;
    else if (above >= GUTTER) top = above;
    else top = Math.max(GUTTER, (vh - ch) / 2);

    let left = rect.left + rect.width / 2 - cw / 2;
    left = Math.min(Math.max(GUTTER, left), vw - cw - GUTTER);

    setPos({ top, left });
  }, [isOpen, rect, index, tick]);

  if (!isOpen || !step) return null;

  const cardStyle = pos
    ? { top: `${pos.top}px`, left: `${pos.left}px` }
    : { top: '-9999px', left: '-9999px' }; // pre-measure frame, never painted

  return createPortal(
    <div className={styles.layer} role="dialog" aria-modal="true" aria-labelledby="rb-tour-title">
      {/* Click-catcher: blocks the app behind the tour without closing it. */}
      <button
        type="button"
        className={styles.catcher}
        aria-hidden="true"
        tabIndex={-1}
        onClick={(e) => e.preventDefault()}
      />

      {/* Dimmer. When a target exists the ring's huge outer shadow does the dimming and
          cuts a lit hole around it; otherwise a plain backdrop covers the screen. */}
      {rect ? (
        <div
          className={styles.ring}
          style={{
            top: `${rect.top - RING_PAD}px`,
            left: `${rect.left - RING_PAD}px`,
            width: `${rect.width + RING_PAD * 2}px`,
            height: `${rect.height + RING_PAD * 2}px`,
          }}
        />
      ) : (
        <div className={styles.backdrop} />
      )}

      <div ref={cardRef} className={styles.card} style={cardStyle}>
        <div className={styles.cardHead}>
          <div className={styles.brandRow}>
            <BrandMark size={26} />
            <span className={styles.tag}>{step.tag}</span>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close tour"
          >
            ✕
          </button>
        </div>

        <h2 id="rb-tour-title" className={styles.title}>{step.title}</h2>
        <p className={styles.body}>{step.body}</p>

        {step.points?.length > 0 && (
          <ul className={styles.points}>
            {step.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <div className={styles.footer}>
          <div className={styles.dots} aria-hidden="true">
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
              />
            ))}
          </div>

          <div className={styles.actions}>
            {isFirst ? (
              <button type="button" className={styles.skip} onClick={onClose}>
                Skip tour
              </button>
            ) : (
              <button type="button" className={styles.ghostBtn} onClick={prev}>
                Previous
              </button>
            )}
            <button type="button" className={styles.primaryBtn} onClick={isLast ? onClose : next}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>

        <div className={styles.counter} aria-hidden="true">
          {index + 1} / {count}
        </div>
      </div>
    </div>,
    document.body,
  );
}
